import { Component, output, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { ProjectService } from '../../../core/services/project.service';
import { CustomStatusService } from '../../../core/services/custom-status.service';
import { FatTemplateService } from '../../../core/services/fat-template.service';
import { DocTypeVersionService } from '../../../core/services/doc-type-version.service';
import { CustomStatus, StatusType } from '../../../core/models/models';
import { Subscription } from 'rxjs';

export const dateRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const availabilityDate = control.get('availabilityDate')?.value;
  const deliveryDate = control.get('deliveryDate')?.value;

  if (availabilityDate && deliveryDate && availabilityDate > deliveryDate) {
    return { dateRangeInvalid: true };
  }
  return null;
};

@Component({
  selector: 'app-project-create-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatIconModule],
  templateUrl: './project-create-dialog.component.html',
  styleUrl: './project-create-dialog.component.scss'
})
export class ProjectCreateDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private projectService = inject(ProjectService);
  private customStatusService = inject(CustomStatusService);
  private fatTemplateService = inject(FatTemplateService);
  private docTypeVersionService = inject(DocTypeVersionService);
  private subscription = new Subscription();

  close = output<void>();
  created = output<void>();

  isValidConfig = signal(true);
  hasValidatedTemplate = signal(true);
  hasValidatedReferential = signal(true);
  statuses: CustomStatus[] = [];

  projectForm = this.fb.group({
    name: ['', Validators.required],
    deliveryAddress: [''],
    city: [''],
    zipCode: [''],
    availabilityDate: ['', Validators.required],
    deliveryDate: ['', Validators.required],
    statusId: [null as number | null, Validators.required]
  }, { validators: dateRangeValidator });

  ngOnInit(): void {
    this.fatTemplateService.hasValidatedTemplate().subscribe({
      next: (res) => this.hasValidatedTemplate.set(res.hasValidated),
      error: () => this.hasValidatedTemplate.set(false)
    });

    this.docTypeVersionService.getValidatedReferentials().subscribe({
      next: (refs) => this.hasValidatedReferential.set(refs.length > 0),
      error: () => this.hasValidatedReferential.set(false)
    });

    this.customStatusService.validateConfig(StatusType.PROJECT).subscribe(isValid => {
      this.isValidConfig.set(isValid);
      if (isValid) {
        this.loadStatuses();
      }
    });

    this.subscription.add(
      this.customStatusService.statuses$.subscribe(data => {
        this.statuses = data;
        if (data.length > 0 && !this.projectForm.get('statusId')?.value) {
          const defaultStatus = data.find(s => s.metaStatus === 'DRAFT');
          if (defaultStatus) {
            this.projectForm.patchValue({ statusId: defaultStatus.id! });
          }
        }
      })
    );

  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private loadStatuses(): void {
    this.customStatusService.getAll(StatusType.PROJECT).subscribe();
  }

  errorMessage = signal('');

  onClose() {
    this.close.emit();
  }

  onSubmit() {
    if (this.projectForm.valid && this.isValidConfig() && this.hasValidatedTemplate() && this.hasValidatedReferential()) {
      this.errorMessage.set('');
      this.projectService.createProject(this.projectForm.value as any).subscribe({
        next: () => {
          this.created.emit();
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message || 'Erreur lors de la création du projet.');
        }
      });
    }
  }
}
